import { useMutation, useQuery } from '@vue/apollo-composable';
import { until } from '@vueuse/core';
import gql from 'graphql-tag';
import { defineStore, storeToRefs } from 'pinia';
import { ref } from 'vue';
import { useRegisterOrderStore } from '@/modules/register/stores/order';
import { useServicesStore } from '@/stores/services';
import unleash from '@/unleash';

export const useRegisterBrowserStore = defineStore('register/browser', () => {
  const showMobileBrowser = ref(false);

  type Favorite = {
    label: string;
    id: number;
    medical: boolean;
    type: 'product' | 'service' | 'service_package';
    price: number;
  };

  const favorites = ref<Favorite[]>([]);
  const loadingFavorites = ref(false);

  const fetchFavorites = () => {
    loadingFavorites.value = true;

    const { onResult } = useQuery(gql`
      query getRegisterItems {
        registerItems(favorite: true) {
          id
          type
          name
          price
          productId
          serviceId
          servicePackageId
          favorite
          medical
        }
      }
    `);

    onResult(({ data: { registerItems } }) => {
      const servicePackagesEnabled = unleash.isEnabled('ServicePackages');

      favorites.value = registerItems
        .filter(
          (item: any) =>
            servicePackagesEnabled || item.type !== 'service_package',
        )
        .map((item: any) => ({
          label: item.name,
          id: item.productId ?? item.serviceId ?? item.servicePackageId,
          medical: item.medical,
          type: item.type,
          price: item.price,
        }));

      loadingFavorites.value = false;
    });
  };

  const { mutate: updateProduct } = useMutation(gql`
    mutation updateProducts($input: UpdateProductInput!) {
      updateProduct(input: $input) {
        product {
          favorite
        }
      }
    }
  `);

  const { mutate: markServiceVariationAsFavorite } = useMutation(gql`
    mutation markServiceVariationAsFavorite(
      $input: MarkServiceVariationAsFavoriteInput!
    ) {
      markServiceVariationAsFavorite(input: $input) {
        service {
          favorite
        }
      }
    }
  `);

  const { mutate: markServicePackageAsFavorite } = useMutation(gql`
    mutation markServicePackageAsFavorite(
      $input: MarkServicePackageAsFavoriteInput!
    ) {
      markServicePackageAsFavorite(input: $input) {
        servicePackage {
          id
          favorite
        }
      }
    }
  `);

  const addFavorite = (item: any) => {
    const { label, id, medical, type, price } = item;
    favorites.value.push({ label, id, medical, type, price });
    updateFavorite(item, true);
  };

  const removeFavorite = (item: Favorite) => {
    favorites.value = favorites.value.filter(
      (favorite) => favorite.type !== item.type || favorite.id !== item.id,
    );
    updateFavorite(item, false);
  };

  const updateFavorite = (item: Favorite, favorite: boolean) => {
    const { id, type } = item;

    if (type === 'service') {
      markServiceVariationAsFavorite({
        input: {
          serviceId: id,
          favorite,
        },
      });
    } else if (type === 'service_package') {
      markServicePackageAsFavorite({
        input: {
          servicePackageId: id,
          favorite,
        },
      });
    } else if (type === 'product') {
      updateProduct({
        input: {
          id,
          favorite,
        },
      });
    }
  };

  const addServicePackageById = async (id: number) => {
    const { fetchingServices, fetchingServicePackages } = storeToRefs(
      useServicesStore(),
    );
    await until(
      () => !fetchingServices.value && !fetchingServicePackages.value,
    ).toBe(true);

    const { servicePackages } = useServicesStore();
    const servicePackage = servicePackages.find(
      (servicePackage) => servicePackage.id === id,
    );
    if (servicePackage) {
      useRegisterOrderStore().addServicePackage(servicePackage);
    }
  };

  return {
    addServicePackageById,
    fetchFavorites,
    favorites,
    addFavorite,
    removeFavorite,
    loadingFavorites,
    showMobileBrowser,
  };
});
